<template>
  <div class="top-nav">
    <div class="top-nav__left">
      <el-breadcrumb separator="/" aria-label="面包屑导航">
        <!-- 面包屑标题可能相同，索引参与键值可避免重复键。 -->
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="`${index}-${item}`">{{ item }}</el-breadcrumb-item>
      </el-breadcrumb>
      <div class="top-nav__title-row">
        <h1 class="top-nav__page-title">{{ pageTitle }}</h1>
        <p class="top-nav__page-desc">{{ pageDescription }}</p>
      </div>
    </div>

    <div class="top-nav__right">
      <el-button
          v-if="showWorkReportShortcut"
          :class="['top-nav__work-report-button', {'is-pending': workReportNeedsAction, 'is-submitted': workReportShortcut.status === 'SUBMITTED'}]"
          :loading="workReportLoading"
          :title="workReportShortcutHint"
          @click="handleWorkReportShortcut"
      >
        <i :class="workReportShortcut.status === 'SUBMITTED' ? 'el-icon-circle-check' : 'el-icon-edit-outline'"></i>
        <span>{{ workReportShortcutLabel }}</span>
        <b v-if="workReportNeedsAction" class="top-nav__work-report-ping" aria-hidden="true"></b>
      </el-button>

      <el-button
          v-if="showWorkReportShortcut"
          :class="['top-nav__work-report-button', {'is-pending': weeklyReportNeedsAction, 'is-submitted': weeklyReportShortcut.status === 'SUBMITTED'}]"
          :loading="workReportLoading"
          :title="weeklyReportShortcutHint"
          @click="openWeeklyReport"
      >
        <i :class="weeklyReportShortcut.status === 'SUBMITTED' ? 'el-icon-circle-check' : 'el-icon-date'"></i>
        <span>{{ weeklyReportShortcutLabel }}</span>
        <b v-if="weeklyReportNeedsAction" class="top-nav__work-report-ping" aria-hidden="true"></b>
      </el-button>

      <el-button
          class="top-nav__guide-button"
          data-guide="guide-entry"
          icon="el-icon-question"
          plain
          @click="$emit('open-guide-center')"
      >
        使用指引
      </el-button>

      <el-dropdown v-if="canSwitchPlatform" class="top-nav__platform" trigger="click" @command="emitSwitch">
        <el-button class="top-nav__switch-button" plain>
          切换端口
          <i class="el-icon-arrow-down el-icon--right"></i>
        </el-button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item v-for="item in platformOptions" :key="item.key" :command="item.key">
            {{ item.label }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>

      <el-button
          v-if="currentUser"
          class="top-nav__calendar-button"
          data-guide="personal-calendar-entry"
          icon="el-icon-date"
          plain
          title="打开个人日程"
          @click="openPersonalCalendar"
      >
        日程
      </el-button>

      <el-popover
          v-model="noticeVisible"
          placement="bottom-end"
          width="380"
          trigger="click"
          popper-class="backstage-notice-popper"
          @show="handleNoticePopoverShow"
      >
        <div class="backstage-notice-preview">
          <div class="backstage-notice-preview__header">
            <div>
              <strong>站内消息</strong>
              <span>{{ unreadCount ? `${unreadCount} 条未读` : '暂无未读' }}</span>
            </div>
            <el-button type="text" :disabled="unreadCount === 0 || readingAll" @click="readAllNotices">
              {{ readingAll ? '处理中…' : '全部已读' }}
            </el-button>
          </div>
          <div v-loading="noticeLoading" class="backstage-notice-preview__body">
            <button
                v-for="notice in noticeRows"
                :key="notice.id"
                type="button"
                :class="['backstage-notice-preview__item', {'is-unread': !notice.read}]"
                @click="openNotice(notice)"
            >
              <span class="backstage-notice-preview__dot"></span>
              <span class="backstage-notice-preview__content">
                <strong>{{ notice.title }}</strong>
                <span>{{ notice.content }}</span>
                <small>{{ notice.eventTypeName }} · {{ notice.createdAt }}</small>
                <em class="backstage-notice-preview__action">{{ noticeActionText(notice) }}<i class="el-icon-arrow-right"></i></em>
              </span>
            </button>
            <el-empty v-if="!noticeLoading && !noticeRows.length" :image-size="56" description="暂无站内消息"/>
          </div>
          <button type="button" class="backstage-notice-preview__all" @click="openAllNotices">查看全部消息</button>
        </div>
        <el-badge slot="reference" :hidden="unreadCount === 0" :value="badgeValue" class="top-nav__badge">
          <el-button circle icon="el-icon-bell" aria-label="站内消息"></el-button>
        </el-badge>
      </el-popover>

      <el-dropdown popper-class="top-nav-user-dropdown" trigger="click" @command="handleUserCommand">
        <div class="top-nav__user">
          <el-avatar icon="el-icon-user-solid" size="small"></el-avatar>
          <div class="top-nav__user-info">
            <strong class="top-nav__user-name">{{ (currentUser && currentUser.displayName) || platform.userName || '用户' }}</strong>
            <span class="top-nav__user-role">{{
                (currentUser && (currentUser.primaryRoleName || currentUser.roleName)) || '后台账号'
              }}</span>
          </div>
          <i class="el-icon-arrow-down"></i>
        </div>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item disabled>{{ (currentUser && currentUser.username) || '未登录' }}</el-dropdown-item>
          <el-dropdown-item disabled class="top-nav__user-scope">
            <span class="scope-label">数据范围</span>
            <span class="scope-value">{{ scopeSummary }}</span>
          </el-dropdown-item>
          <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
  </div>
</template>

<script>
import {
  fetchBackstageNoticePage,
  fetchBackstageNoticeUnreadCount,
  readAllBackstageNotices,
  readBackstageNotice,
} from '@/api/modules/system'
import {
  BACKSTAGE_NOTICE_UPDATED_EVENT,
  notifyBackstageNoticeUpdated,
  resolveAccessibleBackstageNoticePath,
} from '@/utils/backstageNoticeNavigation'
import {displayType} from '@/utils/displayText'
import {fetchWeeklyReportDetail, fetchWorkReport} from '@/api'
import {WORK_REPORT_UPDATED_EVENT} from '@/utils/workReportShortcut'

function localDateString(date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default {
  name: 'TopNav',
  props: {
    platform: {
      type: Object,
      required: true,
    },
    platformOptions: {
      type: Array,
      default: () => [],
    },
    breadcrumbs: {
      type: Array,
      default: () => [],
    },
    currentUser: {
      type: Object,
      default: () => null,
    },
    pageDescription: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      noticeVisible: false,
      noticeLoading: false,
      readingAll: false,
      noticeRows: [],
      unreadCount: 0,
      noticePollingTimer: null,
      workReportShortcut: {},
      weeklyReportShortcut: {},
      workReportLoading: false,
    }
  },
  computed: {
    canSwitchPlatform() {
      // 仅存在多个 Web 端口时才展示切换按钮；合作生态与飞手入口已迁移到小程序。
      return this.platformOptions.length > 1
    },
    pageTitle() {
      return this.breadcrumbs.length ? this.breadcrumbs[this.breadcrumbs.length - 1] : '工作台'
    },
    scopeSummary() {
      if (!this.currentUser) {
        return '授权数据'
      }
      const roleCodes = this.currentUser.roleCodes || [this.currentUser.primaryRoleCode || this.currentUser.roleCode]
      // 超级管理员默认拥有全局数据权限，下拉里只展示摘要，避免重复列出每个业务域。
      if (roleCodes.includes('SUPER_ADMIN')) {
        return '全部数据权限'
      }
      return this.currentUser.effectiveDataScopeSummary || this.currentUser.dataScopeName || '授权数据'
    },
    badgeValue() {
      return this.unreadCount > 99 ? '99+' : this.unreadCount
    },
    showWorkReportShortcut() {
      // 工作汇总是后台登录后的全局快捷操作，切换业务路由后仍保留入口。
      if (!this.currentUser) return false
      const roleCodes = this.currentUser.roleCodes || [this.currentUser.primaryRoleCode, this.currentUser.roleCode]
      const permissionCodes = [
        ...(this.currentUser.menuPermissionCodes || []),
        ...(this.currentUser.permissionCodes || []),
        ...(this.currentUser.buttonPermissionCodes || []),
      ]
      return roleCodes.includes('SUPER_ADMIN') || permissionCodes.includes('*') || permissionCodes.includes('admin:work-reports')
    },
    hasWorkReportManualContent() {
      const report = this.workReportShortcut
      return [report.otherWork, report.nextPlan, report.riskSummary].some(value => String(value || '').trim())
    },
    hasWorkReportSnapshot() {
      return Boolean(this.workReportShortcut && this.workReportShortcut.reportDate)
    },
    workReportNeedsAction() {
      const report = this.workReportShortcut
      return Boolean(this.hasWorkReportSnapshot && report.status !== 'SUBMITTED' && report.relationConfigured && (report.canSubmit || !report.autoItems || !report.autoItems.length || !this.hasWorkReportManualContent))
    },
    workReportShortcutLabel() {
      const report = this.workReportShortcut
      if (!this.hasWorkReportSnapshot || this.workReportLoading) return '今日汇报'
      if (report.status === 'SUBMITTED') return '今日已提交'
      if (!report.relationConfigured) return '日报待配置'
      return report.canSubmit ? '查看并提交' : '写今日汇报'
    },
    workReportShortcutHint() {
      const report = this.workReportShortcut
      if (!this.hasWorkReportSnapshot || this.workReportLoading) return '打开今日工作汇总'
      if (report.status === 'SUBMITTED') return '今日汇总已提交，点击查看详情和点评'
      if (!report.relationConfigured) return '尚未配置点评人，点击查看配置说明'
      if (report.canSubmit) return '系统已整理完成，请先核对内容后再确认提交'
      return '今天还没有可提交的业务记录，点击填写个人日报'
    },
    weeklyReportNeedsAction() {
      return Boolean(this.weeklyReportShortcut.weekStart
          && this.weeklyReportShortcut.status !== 'SUBMITTED'
          && this.weeklyReportShortcut.canSubmit)
    },
    weeklyReportShortcutLabel() {
      const report = this.weeklyReportShortcut
      if (!report.weekStart || this.workReportLoading) return '本周周报'
      if (report.status === 'SUBMITTED') return '周报已提交'
      return report.canSubmit ? '周报待提交' : '写本周周报'
    },
    weeklyReportShortcutHint() {
      const report = this.weeklyReportShortcut
      if (!report.weekStart || this.workReportLoading) return '打开本周周报'
      if (report.status === 'SUBMITTED') return report.reviewed ? '本周周报已点评，点击查看回复' : '本周周报已提交，点击查看详情'
      return report.canSubmit ? '本周周报已具备提交条件，点击核对并提交' : '点击补充本周总结、风险、下周计划或图片'
    },
  },
  watch: {
    currentUser: {
      immediate: true,
      handler(user) {
        this.stopNoticePolling()
        if (user) {
          this.refreshUnreadCount()
          this.startNoticePolling()
          this.refreshWorkReportShortcut()
        } else {
          this.unreadCount = 0
          this.noticeRows = []
          this.workReportShortcut = {}
          this.weeklyReportShortcut = {}
        }
      },
    },
    '$route.name'() {
      this.refreshWorkReportShortcut()
    },
  },
  mounted() {
    window.addEventListener(BACKSTAGE_NOTICE_UPDATED_EVENT, this.handleNoticeUpdated)
    window.addEventListener(WORK_REPORT_UPDATED_EVENT, this.refreshWorkReportShortcut)
  },
  beforeDestroy() {
    this.stopNoticePolling()
    window.removeEventListener(BACKSTAGE_NOTICE_UPDATED_EVENT, this.handleNoticeUpdated)
    window.removeEventListener(WORK_REPORT_UPDATED_EVENT, this.refreshWorkReportShortcut)
  },
  methods: {
    noticeActionText(notice) {
      return notice && notice.eventType === 'WORK_REPORT_COMMENTED' ? '查看点评' : '查看详情'
    },
    emitSwitch(platformKey) {
      this.$emit('switch-platform', platformKey)
    },
    handleUserCommand(command) {
      if (command === 'logout') {
        this.$emit('logout')
      }
    },
    startNoticePolling() {
      this.noticePollingTimer = window.setInterval(() => this.refreshUnreadCount(), 60000)
    },
    stopNoticePolling() {
      if (this.noticePollingTimer) {
        window.clearInterval(this.noticePollingTimer)
        this.noticePollingTimer = null
      }
    },
    async refreshUnreadCount(showError = false) {
      if (!this.currentUser) return
      try {
        const result = await fetchBackstageNoticeUnreadCount()
        this.unreadCount = Number((result && result.count) || 0)
      } catch (error) {
        if (showError) this.$message.error(error.message || '读取站内消息未读数失败')
      }
    },
    async loadNoticePreview() {
      if (!this.currentUser) return
      this.noticeLoading = true
      try {
        const page = await fetchBackstageNoticePage({status: 'ALL', pageNo: 1, pageSize: 8})
        this.noticeRows = ((page && page.rows) || []).map((row) => ({
          ...row,
          eventTypeName: displayType(row.eventType, row.eventTypeName, '系统消息'),
        }))
      } catch (error) {
        this.$message.error(error.message || '读取站内消息失败')
      } finally {
        this.noticeLoading = false
      }
    },
    handleNoticePopoverShow() {
      Promise.all([this.refreshUnreadCount(true), this.loadNoticePreview()])
    },
    handleNoticeUpdated() {
      this.refreshUnreadCount()
      if (this.noticeVisible) this.loadNoticePreview()
    },
    // 顶部全局读取日报状态；读取失败不影响通知、账号切换等其他操作。
    async refreshWorkReportShortcut() {
      if (!this.showWorkReportShortcut || this.workReportLoading) return
      this.workReportLoading = true
      try {
        const results = await Promise.all([fetchWorkReport(localDateString()), fetchWeeklyReportDetail(localDateString())])
        this.workReportShortcut = results[0]
        this.weeklyReportShortcut = results[1]
      } catch (error) {
        this.workReportShortcut = {}
        this.weeklyReportShortcut = {}
      } finally {
        this.workReportLoading = false
      }
    },
    async handleWorkReportShortcut() {
      const report = this.workReportShortcut
      const confirmation = this.getWorkReportShortcutConfirmation(report)
      try {
        await this.$confirm(confirmation.message, confirmation.title, {
          confirmButtonText: confirmation.confirmButtonText,
          cancelButtonText: '取消',
          type: confirmation.type,
        })
      } catch (error) {
        // 用户取消或关闭弹窗时不跳转、不提交，保持当前页面和日报状态不变。
        return
      }
      this.openWorkReport()
    },
    // 快捷入口只负责引导用户核对汇总；实际提交只能在工作汇总页完成。
    openWorkReport() {
      this.$router.push({path: '/admin/work-reports'}).catch((error) => {
        if (!error || error.name !== 'NavigationDuplicated') this.$message.error('打开工作汇总失败，请从左侧菜单重试。')
      })
    },
    // 周报快捷入口只打开编辑页，不在全局导航中直接提交或撤回。
    openWeeklyReport() {
      this.$router.push({path: '/admin/work-reports', query: {tab: 'weekly'}}).catch((error) => {
        if (!error || error.name !== 'NavigationDuplicated') this.$message.error('打开周报失败，请从左侧菜单重试。')
      })
    },
    // 右上角快捷按钮在所有状态下都先明确告知下一步，避免误触直接跳离当前业务页面。
    getWorkReportShortcutConfirmation(report) {
      if (this.hasWorkReportSnapshot && report.relationConfigured && report.canSubmit) {
        return {
          title: '查看今日工作汇总',
          message: '系统已聚合今日业务工作。请先核对自动汇总内容，并按需补充个人日报后，再在工作汇总页确认提交。确定前往吗？',
          confirmButtonText: '查看汇总',
          type: 'info',
        }
      }
      if (report.status === 'SUBMITTED') {
        return {
          title: '打开今日工作汇总',
          message: '今日工作汇总已提交，确定打开详情和点评吗？',
          confirmButtonText: '确定打开',
          type: 'info',
        }
      }
      if (this.hasWorkReportSnapshot && !report.relationConfigured) {
        return {
          title: '查看日报配置说明',
          message: '当前尚未配置点评人，确定进入工作汇总查看配置说明吗？',
          confirmButtonText: '查看说明',
          type: 'warning',
        }
      }
      return {
        title: '打开今日工作汇总',
        message: '确定进入工作汇总，查看自动聚合内容或补充今日汇报吗？',
        confirmButtonText: '确定打开',
        type: 'info',
      }
    },
    async readAllNotices() {
      this.readingAll = true
      try {
        const result = await readAllBackstageNotices()
        this.$message.success(Number(result && result.updatedCount) > 0 ? '站内消息已全部标记为已读' : '当前没有未读消息')
        await Promise.all([this.refreshUnreadCount(), this.loadNoticePreview()])
        notifyBackstageNoticeUpdated()
      } catch (error) {
        this.$message.error(error.message || '全部已读失败')
      } finally {
        this.readingAll = false
      }
    },
    async openNotice(notice) {
      try {
        if (!notice.read) await readBackstageNotice(notice.id)
        notice.read = true
        await this.refreshUnreadCount()
        notifyBackstageNoticeUpdated()
        const target = resolveAccessibleBackstageNoticePath(this.$router, this.currentUser, notice.pagePath)
        this.noticeVisible = false
        if (!target.allowed) {
          if (this.$route.path !== '/admin/notices') await this.$router.push('/admin/notices')
          this.$message.warning(target.reason)
          return
        }
        if (this.$route.fullPath !== target.path) await this.$router.push(target.path)
      } catch (error) {
        this.$message.error(error.message || '处理站内消息失败')
      }
    },
    async openAllNotices() {
      this.noticeVisible = false
      if (this.$route.path !== '/admin/notices') await this.$router.push('/admin/notices')
    },
    // 个人日程是所有后台账号都可使用的个人工具，不依赖业务菜单权限。
    async openPersonalCalendar() {
      if (this.$route.path !== '/admin/personal-calendar') await this.$router.push('/admin/personal-calendar')
    },
  },
}
</script>

<style>
.top-nav__guide-button,.top-nav__calendar-button{border-color:rgba(0,87,183,.24);color:#0057B7;background:#f7faff}.top-nav__guide-button:hover,.top-nav__guide-button:focus,.top-nav__calendar-button:hover,.top-nav__calendar-button:focus{border-color:#0057B7;color:#0057B7;background:#edf5ff}.top-nav__calendar-button:active{color:#003d82;background:#dcecff}@media (max-width:860px){.top-nav__guide-button,.top-nav__calendar-button{padding:9px}.top-nav__guide-button span,.top-nav__calendar-button span{display:none}}
.backstage-notice-popper{padding:0}.backstage-notice-preview{overflow:hidden;border-radius:4px;background:#fff}.backstage-notice-preview__header{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #edf0f5}.backstage-notice-preview__header>div{display:flex;align-items:baseline;gap:8px}.backstage-notice-preview__header strong{color:#1f2d3d;font-size:15px}.backstage-notice-preview__header span{color:#909399;font-size:12px}.backstage-notice-preview__header .el-button{color:#0057B7}.backstage-notice-preview__body{max-height:390px;min-height:120px;overflow-y:auto}.backstage-notice-preview__item{display:flex;width:100%;gap:10px;padding:12px 16px;border:0;border-bottom:1px solid #f0f2f5;background:#fff;text-align:left;cursor:pointer}.backstage-notice-preview__item:hover{background:#f5f9fd}.backstage-notice-preview__item:active{background:#e8f1fb}.backstage-notice-preview__item.is-unread{background:#f4f8fd}.backstage-notice-preview__dot{width:7px;height:7px;margin-top:6px;flex:0 0 auto;border-radius:50%;background:#c6cbd3}.backstage-notice-preview__item.is-unread .backstage-notice-preview__dot{background:#0057B7;box-shadow:0 0 0 3px rgba(0,87,183,.1)}.backstage-notice-preview__content{display:grid;min-width:0;gap:4px}.backstage-notice-preview__content strong,.backstage-notice-preview__content span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.backstage-notice-preview__content strong{color:#1f2d3d;font-size:13px}.backstage-notice-preview__content span{color:#667085;font-size:12px}.backstage-notice-preview__content small{color:#98a2b3;font-size:11px}.backstage-notice-preview__all{width:100%;padding:12px;border:0;background:#fff;color:#0057B7;font-size:13px;cursor:pointer}.backstage-notice-preview__all:hover{background:#f5f9fd}.backstage-notice-preview__all:active{color:#003d82;background:#e8f1fb}
.backstage-notice-preview__action{display:inline-flex;align-items:center;gap:3px;color:#0057B7;font-size:11px;font-style:normal;font-weight:600}.backstage-notice-preview__action i{font-size:10px}
</style>
